import { Box } from "@mui/material";
import { marked } from "marked";
import DOMPurify from "dompurify";

// The /v6/chat model returns Markdown (bold, lists, headings). Render it to
// sanitized HTML — marked parses, DOMPurify strips anything unsafe.
marked.setOptions({ breaks: true });

/** Renders an assistant message written in Markdown, styled for the chat bubble. */
const MarkdownMessage: React.FC<{ text: string }> = ({ text }) => {
  const html = DOMPurify.sanitize(marked.parse(text) as string);
  return (
    <Box
      sx={{
        fontSize: "0.8rem",
        lineHeight: 1.5,
        // Inherit the widget's body gray (C.body, #2a343b) from the parent so
        // reply text — and the bold headers within it — match the rest of the
        // widget instead of MUI's near-black text.primary.
        color: "inherit",
        wordBreak: "break-word",
        "& > :first-of-type": { mt: 0 },
        "& > :last-child": { mb: 0 },
        "& p": { m: 0, mb: 1 },
        "& ul, & ol": { m: 0, mb: 1, pl: 2.25 },
        "& li": { mb: 0.4 },
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontSize: "0.82rem",
          fontWeight: 600,
          m: 0,
          mt: 1.25,
          mb: 0.5,
        },
        "& strong": { fontWeight: 700 },
        "& a": { color: "primary.main", textDecoration: "underline" },
        "& code": {
          fontFamily: "monospace",
          fontSize: "0.78rem",
          bgcolor: "grey.100",
          px: 0.5,
          borderRadius: 0.5,
        },
        "& pre": {
          bgcolor: "grey.100",
          p: 1,
          borderRadius: 1,
          overflowX: "auto",
          "& code": { bgcolor: "transparent", px: 0 },
        },
        "& table": { borderCollapse: "collapse", width: "100%", mb: 1 },
        "& th, & td": {
          border: "1px solid",
          borderColor: "divider",
          px: 0.75,
          py: 0.25,
          textAlign: "left",
        },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default MarkdownMessage;
